import type { Crossing } from './crossing';
import type { TerrainKind } from './elevation';

/**
 * Plain, serializable track data. Everything the simulation needs to know about
 * a circuit lives here; render/ builds meshes from the same data.
 *
 * Conventions (shared with sim/): a standard math 2D frame (x, y) with angles
 * counter-clockwise; "left" is +90 degrees from the tangent. render/ maps the
 * sim's (x, y) to three.js as (x, 0, -y).
 */

export interface Vec2 {
  x: number;
  y: number;
}

export const Surface = {
  Asphalt: 0,
  Curb: 1,
  Grass: 2,
  Gravel: 3,
  /** Artificial turf (or packed earth on the old circuits): a forgiving strip beyond the curb. */
  Turf: 4,
  /** Painted tarmac runoff: nearly as grippy as the track, but off it. */
  Runoff: 5,
} as const;
export type Surface = (typeof Surface)[keyof typeof Surface];

export interface Corner {
  /** Index of first / last sample (may wrap: end < start). */
  start: number;
  end: number;
  apex: number;
  /** +1 left turn, -1 right turn. */
  dir: number;
  /** Total heading change, radians (signed). */
  angle: number;
  /** Smallest centerline radius in the corner, metres. */
  minRadius: number;
  length: number;
}

export interface Straight {
  start: number;
  end: number;
  length: number;
}

export interface BrakingZone {
  /** Sample index where the corner begins (boards count down to here). */
  cornerIndex: number;
  /** Speed drop the bot needs here, m/s. */
  speedDrop: number;
  entrySpeed: number;
  cornerSpeed: number;
  /** Side of the track the corner turns to (+1 left, -1 right). */
  dir: number;
  major: boolean;
}

export interface TrackFeatures {
  longStraight: Straight;
  heavyBraking: BrakingZone;
  /** Every v2 circuit has these three; from v3 on a circuit has whichever its corners make. */
  hairpin: Corner | null;
  chicane: [Corner, Corner] | null;
  sweeper: Corner | null;
  /** Fast bends flicking left-right-left, Silverstone style (v3). */
  esses?: Corner[] | null;
}

export interface TrackData {
  version: number;
  /** Number of centerline samples; sample 0 is the start/finish line. */
  n: number;
  /** Total centerline length, metres. */
  length: number;
  halfWidth: number;
  curbWidth: number;
  /** Centerline positions. */
  px: number[];
  py: number[];
  /** Unit tangents. */
  tx: number[];
  ty: number[];
  /** Arc length at each sample. */
  s: number[];
  /** Signed curvature (left positive), smoothed, 1/m. */
  curvature: number[];
  /** 1 where a curb lines that side of the asphalt. */
  curbLeft: number[];
  curbRight: number[];
  /** Runoff surface beyond the curb: Grass or Gravel. */
  runoffLeft: number[];
  runoffRight: number[];
  /** Lateral distance from the centerline to the barrier on each side. */
  barrierLeft: number[];
  barrierRight: number[];
  /** Sector boundaries as sample indices: sector 1 = [0, s1), 2 = [s1, s2), 3 = [s2, n). */
  sectorIndex: [number, number];
  corners: Corner[];
  straights: Straight[];
  brakingZones: BrakingZone[];
  features: TrackFeatures;
  /** Where "restart" drops the car: metres before the line, and its speed. */
  spawnDistance: number;
  spawnSpeed: number;
  /** v3: the spawn sits on the racing line, this far left of the centreline. */
  spawnOffset?: number;
  /** Height of the centreline at each sample, metres (all 0 on a flat day). The road has no camber. */
  elev: number[];
  terrain: TerrainKind;
  /** Figure-of-eight circuits (v3): where the track crosses itself, one pass under the other. */
  crossing?: Crossing | null;
  /** v3: banking per sample, as the slope across the road (tan of the angle), left side higher positive. Absent = none. */
  bank?: number[];
  /**
   * Runoff aprons (from 30 September 2026): per sample and side, the surface just
   * beyond the curb (or the asphalt edge) where the bot runs to the white line:
   * 0 none, Surface.Curb (a flat second kerb, still track), Surface.Turf or
   * Surface.Runoff; and its width in metres.
   */
  apronLeft?: number[];
  apronRight?: number[];
  apronWidthLeft?: number[];
  apronWidthRight?: number[];
  /** How the runoff is dressed: by era (old sand and earth, then gravel and turf, then painted tarmac). */
  runoffStyle?: 'classic' | 'mid' | 'modern';
  /** v3: the day's showpiece and where it is (arc lengths; `to` < `from` when it wraps past the line). */
  signature?: string;
  sections?: { name: string; from: number; to: number }[];
}

export interface MedalTimes {
  author: number;
  gold: number;
  silver: number;
  bronze: number;
}
